import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { OutcomeCard } from "@/components/outcome-card";
import { useCatalog } from "@/hooks/use-catalog";
import { useCategories } from "@/hooks/use-categories";
import { cn } from "@/lib/utils";

export function CatalogPage() {
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const categorySlug = searchParams.get("category") ?? undefined;

  const { data, isLoading, error } = useCatalog({ category: categorySlug });
  const { data: categories } = useCategories();

  const items = (data?.items ?? []).filter((o) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      o.name.toLowerCase().includes(q) ||
      o.partner_name.toLowerCase().includes(q) ||
      (o.description ?? "").toLowerCase().includes(q)
    );
  });

  function setCategory(slug: string | undefined) {
    if (slug) setSearchParams({ category: slug });
    else setSearchParams({});
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <header className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Link to="/" className="text-sm text-neutral-500 hover:underline">
            ← Home
          </Link>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight">Catalog</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Finished work products from curated AppSumo partners.
          </p>
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search outcomes or partners…"
          className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none dark:border-neutral-700 dark:bg-neutral-900 sm:max-w-xs"
        />
      </header>

      <div className="mt-10 flex flex-col gap-6 lg:flex-row">
        {/* Category sidebar */}
        <aside className="lg:w-48 lg:shrink-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            Categories
          </p>
          <ul className="mt-3 space-y-1">
            <CategoryButton
              active={!categorySlug}
              onClick={() => setCategory(undefined)}
            >
              All outcomes
            </CategoryButton>
            {categories?.map((cat) => (
              <CategoryButton
                key={cat.slug}
                active={categorySlug === cat.slug}
                onClick={() => setCategory(cat.slug)}
              >
                {cat.icon ? <span className="mr-1.5">{cat.icon}</span> : null}
                {cat.name}
              </CategoryButton>
            ))}
          </ul>
        </aside>

        {/* Grid */}
        <section className="min-w-0 flex-1">
          {isLoading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-44 animate-pulse rounded-xl bg-neutral-100 dark:bg-neutral-900"
                />
              ))}
            </div>
          ) : error ? (
            <p className="text-sm text-red-500">
              Couldn't load the catalog: {(error as Error).message}
            </p>
          ) : items.length === 0 ? (
            <p className="text-sm text-neutral-500">
              No outcomes match your search.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((o) => (
                <OutcomeCard key={o.id} outcome={o} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function CategoryButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <li>
      <button
        onClick={onClick}
        className={cn(
          "w-full rounded-md px-3 py-1.5 text-left text-sm transition",
          active
            ? "bg-emerald-500 font-medium text-white"
            : "text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-900",
        )}
      >
        {children}
      </button>
    </li>
  );
}
